<!-- These are fine as-is, the TypeScript will add the ol- classes -->
<div
  #snapshotButtonContainer
  class="ol-snapshot-button-container"
  style="display: none"
>
  <button class="snapshot-btn" (click)="takeSnapshot()">
    <i class="pi pi-camera"></i>
  </button>
</div>

<div
  #projectionToggleContainer
  class="ol-projection-toggle-container"
  style="display: none"
>
  <button class="projection-toggle-btn" (click)="toggleProjection()">
    <i class="pi pi-globe"></i>
    {{ currentProjection === "EPSG:4326" ? "Terrain" : "Base Map" }}
  </button>
</div>

<!-- Snapshot Modal -->
<div
  class="snapshot-modal"
  [class.active]="showSnapshotModal"
  (click)="closeModal($event)"
>
  <div class="snapshot-container">
    <h3>{{ snapshotTitle }}</h3>
    @if (snapshotImageSrc) {
      <img [src]="snapshotImageSrc" alt="Map Snapshot" class="snapshot-img" />
    }
    <div class="snapshot-actions">
      <button class="snapshot-download-btn" (click)="downloadSnapshot()">
        <i class="pi pi-download"></i>
        Download
      </button>
      <button class="snapshot-close-btn" (click)="closeSnapshotModal()">
        <i class="pi pi-times"></i>
        Close
      </button>
    </div>
  </div>
</div>

<!-- Metric dropdown -->
<div
  #metricDropdownContainer
  class="ol-metric-dropdown-container ol-select-container ol-unselectable ol-control"
  style="display: none"
>
  <div class="flex flex-column">
    <span class="p-float-label">
      <p-select
        id="metricSelector-map"
        [options]="metrics"
        [(ngModel)]="selectedMetric"
        optionLabel="metric_pretty_name"
        (onChange)="onMetricChange($event)"
        [style]="{ width: '100%' }"
      >
      </p-select>
    </span>
  </div>
</div>

<!-- Layer dropdown -->
<div
  #layerDropdownContainer
  class="ol-layer-dropdown-container ol-select-container ol-unselectable ol-control"
  style="display: none"
>
  @if (getAvailableLayersForCurrentMetric()?.length > 1) {
    <div class="flex flex-column">
      <span class="p-float-label">
        <p-select
          id="layer-select"
          [options]="getAvailableLayersForCurrentMetric()"
          [(ngModel)]="
            selectedLayerForMetric[selectedMetric?.metric_query_name]
          "
          optionLabel="name"
          [style]="{ width: '100%' }"
          (onChange)="
            onLayerSelectionChange(
              selectedMetric?.metric_query_name,
              $event.value
            )
          "
        >
        </p-select>
      </span>
      @if (!getAvailableLayersForCurrentMetric()?.length) {
        <div class="p-field-description mt-2">
          No layers available for selection.
        </div>
      }
    </div>
  }
</div>

<!-- Map Container -->
<div class="map-wrapper">
  <div
    class="map-container"
    #mapContainer
    [class.map-loading]="isLoading"
    style="width: 100%; aspect-ratio: 3 / 2"
    id="imap"
  >
    <!-- Loading Overlay -->
    @if (isLoading) {
      <div class="map-loading-overlay">
        <div class="spinner-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
          <div class="loading-content">
            <span class="loading-text">Loading map data...</span>
            <span class="loading-subtext">Please wait</span>
          </div>
        </div>
      </div>
    }

    <!-- Editable colorbar -->
    @if (colorbarState && !colorbarState.is_categorical) {
      <div class="colorbar-container">
        <div class="colorbar-wrapper">
          <div
            class="colorbar-gradient"
            [style.background]="colorbarState.gradient"
          ></div>
          <div class="colorbar-labels">
            <!-- Editable Min -->
            @if (!editingMin) {
              <span
                class="colorbar-min clickable"
                (click)="startEditMin()"
                title="Click to edit minimum value"
              >
                {{ colorbarState.vmin | number: "1.0-3" }}
              </span>
            }
            @if (editingMin) {
              <input
                class="colorbar-min-input"
                type="number"
                [value]="colorbarState.vmin"
                (keydown.enter)="confirmMin($any($event.target).value)"
                (keydown.escape)="cancelEditMin()"
                (blur)="confirmMin($any($event.target).value)"
                step="0.01"
              />
            }
            <!-- Intermediate ticks -->
            @for (tick of getColorbarTicks(); track tick) {
              <span
                class="colorbar-tick"
                [style.left]="
                  'calc(' +
                  tick.percent +
                  '% - ' +
                  ((tick.percent / 100) * 120 - 60) +
                  'px)'
                "
              >
                {{ tick.value | number: "1.0-3" }}
              </span>
            }
            <!-- Metric Name -->
            <span class="colorbar-title">
              {{ colorbarState.metric_name | titlecase
              }}{{ colorbarState.metrics_description ?? "" }}
            </span>
            <!-- Editable Max -->
            @if (!editingMax) {
              <span
                class="colorbar-max clickable"
                (click)="startEditMax()"
                title="Click to edit maximum value"
              >
                {{ colorbarState.vmax | number: "1.0-3" }}
              </span>
            }
            @if (editingMax) {
              <input
                class="colorbar-max-input"
                type="number"
                [value]="colorbarState.vmax"
                (keydown.enter)="confirmMax($any($event.target).value)"
                (keydown.escape)="cancelEditMax()"
                (blur)="confirmMax($any($event.target).value)"
                step="0.01"
              />
            }
          </div>
        </div>
        <!-- Reset button: only visible when user has overridden the range -->
        @if (colorbarState.isCustomRange) {
          <button
            class="colorbar-reset-btn"
            (click)="resetColorbarRange()"
            title="Reset to original range"
          >
            &#x21BA;
          </button>
        }
      </div>
    }

    <!-- Point Value Dialog -->
    <p-dialog
      [(visible)]="pointPopupVisible"
      (onHide)="onPopupClose()"
      [modal]="false"
      [draggable]="true"
      [resizable]="false"
      [closable]="true"
      [closeOnEscape]="true"
      [style]="{ width: '350px' }"
      [appendTo]="mapContainer"
    >
      <ng-template pTemplate="header">
        <div class="dialog-header-custom">
          <span class="dialog-title">{{ getDialogHeader() }}</span>
          @if (getDialogSubheader()) {
            <span
              class="dialog-subtitle"
              [innerHTML]="getDialogSubheader()"
            ></span>
          }
        </div>
      </ng-template>

      <!-- Loading State -->
      @if (pointQueryLoading) {
        <div class="dialog-state">
          <p-progressSpinner
            [style]="{ width: '40px', height: '40px' }"
            strokeWidth="4"
          >
          </p-progressSpinner>
          <p class="loading-text">Loading data...</p>
        </div>
      }

      <!-- Candidate Selection State -->
      @if (!pointQueryLoading && showCandidateSelection) {
        <div class="dialog-content">
          <div class="info-section">
            <p class="selection-prompt">
              Multiple points found. Please select one:
            </p>
          </div>
          <div class="candidate-list">
            @for (
              candidate of candidates;
              track trackByGpi($index, candidate)
            ) {
              <button
                (click)="selectCandidate(candidate)"
                class="candidate-button"
                type="button"
              >
                <div class="candidate-primary">
                  <span class="candidate-name">{{
                    getCandidateName(candidate)
                  }}</span>
                  <span class="candidate-value">{{ candidate.value }}</span>
                </div>
                <div class="candidate-meta">
                  @if (isISMNData && candidate.instrument) {
                    <span class="meta-item">
                      <span class="meta-label">Sensor:</span>
                      {{ candidate.instrument }}
                    </span>
                  }
                  <span class="meta-item">
                    <span class="meta-label">Lat:</span>
                    {{ candidate.lat.toFixed(3) }}°
                  </span>
                  <span class="meta-item">
                    <span class="meta-label">Lon:</span>
                    {{ candidate.lon.toFixed(3) }}°
                  </span>
                  <span class="meta-item">
                    <span class="meta-label">ID:</span> {{ candidate.gpi }}
                  </span>
                </div>
              </button>
            }
          </div>
        </div>
      }

      <!-- Success State - ISMN Case -->
      @if (
        !pointQueryLoading &&
        !showCandidateSelection &&
        selectedPointValue !== null &&
        isISMNData
      ) {
        <div class="dialog-content">
          @if (multipleFound) {
            <div class="info-section back-section">
              <button
                (click)="showCandidateSelection = true; cdr.detectChanges()"
                class="p-button p-button-text p-button-sm back-button"
                type="button"
              >
                <i class="pi pi-arrow-left"></i>
                <span>Back to selection ({{ candidates.length }} points)</span>
              </button>
            </div>
          }
          <div class="info-section">
            <div class="label-value highlight">
              <span class="label">{{ varName || "Value" }}</span>
              <span class="value large">{{ selectedPointValue }}</span>
            </div>
          </div>
          <div class="info-section compact">
            <div class="label-value">
              <span class="label">Latitude</span>
              <span class="value"
                >{{ selectedPointCoords?.lat | number: "1.0-2" }}°</span
              >
            </div>
            <div class="label-value">
              <span class="label">Longitude</span>
              <span class="value"
                >{{ selectedPointCoords?.lon | number: "1.0-2" }}°</span
              >
            </div>
          </div>
          @if (landcoverType || climateKG || frmClass) {
            <div class="info-section metadata">
              @if (landcoverType) {
                <div class="label-value">
                  <span class="label">Land Cover</span>
                  <span class="value">{{ landcoverType }}</span>
                </div>
              }
              @if (climateKG) {
                <div class="label-value">
                  <span class="label">Climate</span>
                  <span class="value">{{ climateKG }}</span>
                </div>
              }
              @if (frmClass) {
                <div class="label-value">
                  <span class="label">FRM Class</span>
                  <span class="value">{{ frmClass }}</span>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Success State - Gridded Data Case -->
      @if (
        !pointQueryLoading &&
        !showCandidateSelection &&
        selectedPointValue !== null &&
        !isISMNData
      ) {
        <div class="dialog-content">
          @if (multipleFound) {
            <div class="info-section back-section">
              <button
                (click)="showCandidateSelection = true; cdr.detectChanges()"
                class="p-button p-button-text p-button-sm back-button"
                type="button"
              >
                <i class="pi pi-arrow-left"></i>
                <span>Back to selection ({{ candidates.length }} points)</span>
              </button>
            </div>
          }
          <div class="info-section">
            <div class="label-value highlight">
              <span class="label">{{
                selectedMetric?.metric_query_name || "Value"
              }}</span>
              <span class="value large">{{ selectedPointValue }}</span>
            </div>
          </div>
          <div class="info-section compact">
            <div class="label-value">
              <span class="label">Latitude</span>
              <span class="value"
                >{{ selectedPointCoords?.lat | number: "1.0-4" }}°</span
              >
            </div>
            <div class="label-value">
              <span class="label">Longitude</span>
              <span class="value"
                >{{ selectedPointCoords?.lon | number: "1.0-4" }}°</span
              >
            </div>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (
        !pointQueryLoading &&
        !showCandidateSelection &&
        selectedPointValue === null
      ) {
        <div class="dialog-state">
          <i
            class="pi pi-info-circle"
            style="font-size: 2rem; color: #6c757d"
          ></i>
          <p class="error-text">
            {{ pointErrorMessage || "No data available at this location" }}
          </p>
        </div>
      }
    </p-dialog>
  </div>
</div>
